import { createFetch } from '@vueuse/core';
import { computed } from 'vue';
import useErrorToasts from '@/composables/useErrorToasts';
import { USER_API } from '@/config/appConfig';

import useAuthStore from '@/stores/auth';

const plansFetch = createFetch({
  baseUrl: USER_API,
  options: {
    async onFetchError(payload) {
      useErrorToasts(payload);
      return payload;
    },
    beforeFetch({ options, cancel }) {
      const authStore = useAuthStore();

      if (!authStore.currentTeamId) cancel();
      // eslint-disable-next-line no-param-reassign
      options.headers['Tgb-Token'] = authStore.userToken;
    },
  },
  fetchOptions: {
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
  },
});

export default () => ({
  getTeams: (userId, fetchOptions = {}) => {
    const url = computed(() => `account/owners/${userId.value}`);

    return plansFetch(url, fetchOptions).get().json();
  },

  getTeamMembers: (userId, fetchOptions = {}) => {
    const url = computed(() => `account/admins/${userId.value}`);

    return plansFetch(url, fetchOptions).get().json();
  },

  removeTeamMember: (requestData, fetchOptions = {}) => {
    const url = '/account/admin';

    return plansFetch(url, fetchOptions).post({
      owner_id: requestData.ownerId,
      admin_id: requestData.adminId,
      operation: 'remove',
    }).json();
  },

  addTeamMember: (requestData, fetchOptions = {}) => {
    const url = '/account/admin';

    return plansFetch(url, fetchOptions).post({
      owner_id: requestData.ownerId,
      admin_id: requestData.adminId,
      operation: 'add',
    }).json();
  },

  getPlan: (fetchOptions = {}) => {
    const url = computed(() => '/plan/check');

    return plansFetch(url, fetchOptions).get().json();
  },

  /**
   * Get list of permissions by owner.
   * @param {Number} owner - ID of the owner of the account.
   * @param {Array<String>} permissions - List of permissions we want to fetch.
   * @param {Object} options - Additional fetch options.
   * @returns {Promise<Object>} API response. Map of requested permissions.
   */
  checkPlanPermissions: (owner, permissions = [], options = {}) => {
    const url = computed(() => `/plan/permissions/check?owner=${owner.value}&permissions=${permissions.join(',')}`);
    return plansFetch(url, options).get().json();
  },

  /**
   * Update plan permissions for an account owner.
   * @param {Number|String} ownerId - ID of the owner of the account.
   * @param {Object} permissions - Permissions object to update (e.g., { optimize_images: true }).
   * @param {Object} options - Additional fetch options.
   * @returns {Promise<Object>} API response.
   */
  updatePlanPermissions: (ownerId, permissions, options = {}) => {
    const url = '/plan/permissions/update';
    return plansFetch(url, options).post({
      owner_id: ownerId,
      permissions,
    }).json();
  },
});
